import React, {
  ReactElement,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import {
  faMinus,
  faPlus,
  faQuestionCircle,
  faSortDown,
} from '@fortawesome/pro-solid-svg-icons';
import { faCog, faTrash, faUsers } from '@fortawesome/pro-regular-svg-icons';
import {
  CourseEnrolment,
  InstitutionEnrolment,
} from 'src/ReduxStorage/InstitutionAdminSettings/types';
import { Link } from 'src/web/containers/Link';
import { ROUTE_USER_PROFILE_PAGE } from 'src/ReduxStorage/routeActions';
import moment from 'moment';
import { PortalResourceGetPaginatedEnrolments } from 'src/resource/PortalResource/types';
import { Paginate } from 'src/web/components/Pagination/Paginate';
import { Spinner } from 'src/web/components/Spinner/Spinner';
import { debounce } from 'lodash';
import { ValueLabelPair } from 'src/web/components/Input/DropdownsOld/SingleSelect/types';
import { ActionTable } from 'src/web/components/ActionTable/component';
import { getPaginatedEnrolmentsApi } from 'src/ReduxStorage/InstitutionAdminSettings/asyncActions';
import { pNotifyAlert } from 'src/web/components/Pnotify/component';
import { Key, Row, SortBy } from '../../../../../components/ActionTable/types';
import {
  PaginationContainer,
  FilterWrapper,
  ActionTableWrapper,
  SpinnerWrapper,
  BulkActionContentWrapper,
  BulkActionContentButton,
  BulkActionButton,
  BulkActionSortDownIcon,
  PopoverContentHeader,
  PopoverContentBody,
  QuestionCircleIcon,
  NameEmailInfo,
  BulkActionIcon,
} from '../../../styles';
import {
  UserEmail,
  CourseAssignedSelect,
  InstitutionRoleSelect,
  GroupName,
  TableHeading,
  FilterByUserSelect,
} from './styles';
import { Popover } from '../../../../../components/Popover/component';
import {
  useAddEnrolmentsToUserGroupsApi,
  useRemoveEnrolmentsApi,
  useSuggestUserApi,
  useUpdateRoleForUsersApi,
  useScheduleAssignToCourseApi,
  useRemoveFromCourseApi,
} from '../../../hooks';
import { AddToGroupModal } from '../../../components/AddToGroupsModal/component';
import { RemoveFromInstitutionModal } from '../../../components/RemoveFromInstitutionModal/component';
import { ChangeInstitutionRoleModal } from '../ChangeInstitutionRoleModal/component';
import { RemoveFromCoursesModal } from '../../../components/RemoveFromCoursesModal/component';
import { AssignToCoursesModal } from '../AssignToCoursesModal/component';

import { ConnectedCoursesAssignedPopover } from '../../../components/CourseAssignedPopover/connector';
import { AssignedCourseRolesType } from '../../../types';
import { parseKeyDataActiveMember } from '../../../helpers';

const GroupsPopover = ({
  userGroups,
}: {
  userGroups: readonly string[];
}): ReactElement => {
  if (userGroups.length === 0) {
    return <>0</>;
  }

  return (
    <Popover
      toggle={
        <span>
          {userGroups.length} <QuestionCircleIcon icon={faQuestionCircle} />
        </span>
      }
      placement="bottom"
      popOverInitialState={{
        unstable_preventOverflow: false,
        unstable_fixed: true,
      }}
      content={
        <div>
          <PopoverContentHeader>Groups</PopoverContentHeader>
          <PopoverContentBody>
            {userGroups.map((group) => (
              <GroupName>{group}</GroupName>
            ))}
          </PopoverContentBody>
        </div>
      }
    />
  );
};

export interface ActiveTeamMemberProps {
  enrolments: readonly InstitutionEnrolment[];
  total: number;
  totalPages: number;
  isLoadingData: boolean;
  currentInstitutionPath: string;
  allCourses: CourseEnrolment[];
  filterByUserEmails?: string[];
  getPaginatedEnrolments: (
    params: PortalResourceGetPaginatedEnrolments['args']
  ) => Promise<unknown>;
}

enum InstitutionMemberRole {
  ALL = 'ALL',
  ADMIN = 'ADMINISTRATOR',
  STAFF = 'STAFF',
}

enum ShowedModalType {
  ADD_TO_GROUPS = 'ADD_TO_GROUPS',
  REMOVE_FROM_INSTITUTION = 'REMOVE_FROM_INSTITUTION',
  CHANGE_INSTITUTION_ROLE = 'CHANGE_INSTITUTION_ROLE',
  REMOVE_FROM_COURSES = 'REMOVE_FROM_COURSES',
  ASSIGN_TO_COURSES = 'ASSIGN_TO_COURSES',
}

const ADMIN_NAMING = 'Institution administrator';
const STAFF_NAMING = 'Course creator';

const INSTITUTION_SELECT_ROLE_OPTIONS = [
  {
    value: InstitutionMemberRole.ALL,
    label: 'All',
  },
  {
    value: InstitutionMemberRole.ADMIN,
    label: ADMIN_NAMING,
  },
  {
    value: InstitutionMemberRole.STAFF,
    label: STAFF_NAMING,
  },
];

export const ActiveTeamMemberTable: React.FC<ActiveTeamMemberProps> = ({
  enrolments,
  total,
  totalPages,
  isLoadingData,
  currentInstitutionPath,
  allCourses,
  filterByUserEmails, // list of emails to filter by email in querying the enrolment
  getPaginatedEnrolments,
}) => {
  // selectedRows format `<enrolmentId>-<userId>'
  const [selectedRows, setSelectedRows] = useState<Key[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy | undefined>({
    sortKey: 'courseEnrolmentCount',
    direction: 'descending',
  });
  const [selectedRole, setSelectedRole] = useState<ValueLabelPair>(
    INSTITUTION_SELECT_ROLE_OPTIONS[0]
  );
  const [
    showedModalType,
    setShowedModalType,
  ] = useState<ShowedModalType | null>(null);
  const [enrolmentsQueryParams, setEnrolmentsQueryParams] = useState<{
    pageIndex: number;
    pageSize: number;
    filterByStaff: boolean;
    filterByAdmin?: boolean;
    filterByUserId?: string;
    sortBy?: string;
    sortDirection?: number;
    filterByCoursePaths?: string[];
    filterByUserEmails?: string[];
  }>({
    pageIndex: 1,
    pageSize: 10,
    filterByStaff: true,
    filterByUserEmails,
    sortBy: 'courseEnrolmentCount',
    sortDirection: -1,
  });
  const [
    coursesAssignedSelectedValue,
    setCoursesAssignedSelectedValue,
  ] = useState<ValueLabelPair>({
    value: 'all',
    label: 'All',
  });

  const [suggestUserQueryResult, setSearchUserQuery] = useSuggestUserApi(
    enrolmentsQueryParams.filterByUserId,
    currentInstitutionPath,
    true
  );

  // Bulk action helpers initiation
  const { addEnrolmentsToUserGroups } = useAddEnrolmentsToUserGroupsApi(
    currentInstitutionPath
  );
  const { removeEnrolments } = useRemoveEnrolmentsApi(currentInstitutionPath);
  const { updateRoleForUsers } = useUpdateRoleForUsersApi(
    currentInstitutionPath
  );
  const { scheduleAssignToCourse } = useScheduleAssignToCourseApi(
    currentInstitutionPath,
    true
  );
  const { removeFromCourse } = useRemoveFromCourseApi(currentInstitutionPath);

  const { pageIndex, pageSize } = enrolmentsQueryParams;
  const totalSelectedMembers = selectAll ? total : selectedRows.length;

  const setPageIndex = (pageIndex: number): void => {
    setEnrolmentsQueryParams((prevState) => ({
      ...prevState,
      pageIndex,
    }));
  };

  const setPageSize = (pageSize: number): void => {
    setEnrolmentsQueryParams((prevState) => ({
      ...prevState,
      // Reset page index to 1 every pageSize changes
      pageIndex: 1,
      pageSize,
    }));
  };

  const debouncedGetPaginatedEnrolments = useCallback(
    debounce(
      (params: PortalResourceGetPaginatedEnrolments['args']) =>
        getPaginatedEnrolments(params),
      300
    ),
    []
  );

  useEffect(() => {
    let sortingParams: { sortBy?: string; sortDirection?: -1 | 1 } = {};
    if (!sortBy) {
      sortingParams = {
        sortBy: undefined,
        sortDirection: undefined,
      };
    } else {
      sortingParams = {
        sortBy: sortBy.sortKey,
        sortDirection: sortBy.direction === 'ascending' ? 1 : -1,
      };
    }
    setEnrolmentsQueryParams((prev) => ({
      ...prev,
      ...sortingParams,
    }));
  }, [sortBy]);

  useEffect(() => {
    let filterRoles: { filterByAdmin?: boolean; filterByStaff?: boolean } = {};
    switch (selectedRole.value) {
      case InstitutionMemberRole.ALL:
        filterRoles = {
          filterByStaff: true,
          filterByAdmin: undefined,
        };
        break;
      case InstitutionMemberRole.ADMIN:
        filterRoles = {
          filterByAdmin: true,
          filterByStaff: undefined,
        };
        break;
      case InstitutionMemberRole.STAFF:
        filterRoles = {
          filterByAdmin: false,
          filterByStaff: true,
        };
        break;
      default:
        break;
    }
    setEnrolmentsQueryParams((prev) => ({
      ...prev,
      ...filterRoles,
      pageIndex: 1,
    }));
  }, [selectedRole.value]);

  useEffect(() => {
    // Page index data is zero-index, so we need to minus it by one
    debouncedGetPaginatedEnrolments({
      ...enrolmentsQueryParams,
      pageIndex: enrolmentsQueryParams.pageIndex - 1,
    });
  }, [enrolmentsQueryParams]);

  const activeMembersrows = useMemo((): Row[] => {
    const ret: Row[] = [];
    for (let i = 0; i < enrolments.length; i++) {
      const {
        id,
        user,
        isAdmin,
        staff,
        enrolments: courseEnrolments,
        lastActive,
        userGroups,
      } = enrolments[i];
      const { id: userId } = user;
      ret.push({
        // key use this pattern `<enrolmentId>-<userId>'
        key: `${id}-${userId}`,
        cells: [
          {
            cell: (
              <NameEmailInfo>
                <img alt="avatar" src={user.standardAvatar} />
                <div>
                  <Link
                    to={{
                      type: ROUTE_USER_PROFILE_PAGE,
                      params: {
                        profileName: user.profileName,
                      },
                    }}
                    target="_blank"
                  >
                    {user.fullName}
                  </Link>
                  <UserEmail>{user.emailAddress}</UserEmail>
                </div>
              </NameEmailInfo>
            ),
          },
          {
            cell: isAdmin ? ADMIN_NAMING : staff ? STAFF_NAMING : '',
          },
          {
            cell: user.institutionMembershipId ?? 'N/A',
          },
          {
            cell: (
              <ConnectedCoursesAssignedPopover
                memberType="team-member"
                institutionPath={currentInstitutionPath}
                coursePaths={courseEnrolments}
                currentUserId={id}
                scheduleAssignToCourse={scheduleAssignToCourse}
              />
            ),
          },
          {
            cell: <GroupsPopover userGroups={userGroups} />,
          },
          {
            cell: lastActive ? moment(lastActive).format('D MMMM YYYY') : 'N/A',
          },
        ],
      });
    }
    return ret;
  }, [enrolments]);

  const hideModal = useCallback((): void => {
    setShowedModalType(null);
  }, []);
  const [isBulkActionRunning, setIsBulkActionRunning] = useState(false);
  const [bulkActionError, setBulkActionError] = useState('');

  const handleScheduleAssignToCourse = async (
    courseRoles: AssignedCourseRolesType
  ): Promise<void> => {
    try {
      setIsBulkActionRunning(true);
      setBulkActionError('');
      await scheduleAssignToCourse({
        ids: selectedRows.map((key) => parseKeyDataActiveMember(key).userId),
        courseRoles,
        totalAssignedUsers: totalSelectedMembers,
        filterParams: {
          all: selectAll,
          filterByAdmin: enrolmentsQueryParams.filterByAdmin,
          filterByStaff: enrolmentsQueryParams.filterByStaff,
          filterByCoursePaths: enrolmentsQueryParams.filterByCoursePaths,
        },
      });
      pNotifyAlert({
        title: 'Success',
        text: `Adding ${
          selectAll ? total : selectedRows.length
        } members to courses. You can check the progress on the Recently assigned courses tab`,
        type: 'success',
      });
      hideModal();
    } catch (error) {
      setBulkActionError(error?.message);
    }
    setIsBulkActionRunning(false);
  };

  const bulkActionWrapper = useCallback(
    async <T extends { ids: string[] }>(
      bulkActionCallbackApi: (params: T) => Promise<unknown>,
      restParams: Omit<T, 'ids'>,
      pageSizeLimit: number,
      identifierKey: 'enrolmentId' | 'userId' = 'enrolmentId'
    ) => {
      try {
        setIsBulkActionRunning(true);
        setBulkActionError('');
        if (selectAll) {
          let currentPageIndex = 0;
          let hasNextPageEnrolments = true;
          while (hasNextPageEnrolments) {
            // do bulk action in batch per pageSizeLimit
            // eslint-disable-next-line no-await-in-loop
            const enrolmentsData = await getPaginatedEnrolmentsApi(
              currentInstitutionPath,
              {
                ...enrolmentsQueryParams,
                pageSize: pageSizeLimit,
                pageIndex: currentPageIndex,
              }
            );
            const { hasNextPage, enrolments } = enrolmentsData;
            const ids = enrolments.map(({ id, user: { id: userId } }) =>
              identifierKey === 'enrolmentId'
                ? id
                : identifierKey === 'userId'
                ? userId
                : ''
            );
            // eslint-disable-next-line no-await-in-loop
            await bulkActionCallbackApi({
              ...restParams,
              ids,
            } as T);

            hasNextPageEnrolments = hasNextPage;
            currentPageIndex += 1;
          }
        } else {
          await bulkActionCallbackApi({
            ...restParams,
            ids: selectedRows.map((key) => {
              const { enrolmentId, userId } = parseKeyDataActiveMember(key);
              return identifierKey === 'enrolmentId'
                ? enrolmentId
                : identifierKey === 'userId'
                ? userId
                : '';
            }),
          } as T);
        }
        setIsBulkActionRunning(false);
        hideModal();
        debouncedGetPaginatedEnrolments({
          ...enrolmentsQueryParams,
          pageIndex: pageIndex - 1,
        });
      } catch (error) {
        setIsBulkActionRunning(false);
        setBulkActionError(error?.message);
      }
    },
    [
      selectAll,
      currentInstitutionPath,
      enrolmentsQueryParams,
      debouncedGetPaginatedEnrolments,
      hideModal,
      pageIndex,
      selectedRows,
    ]
  );

  const ShowedModal = useMemo(() => {
    switch (showedModalType) {
      case ShowedModalType.ADD_TO_GROUPS:
        return (
          <AddToGroupModal
            isAddingToGroupLoading={isBulkActionRunning}
            onAddToGroup={(userGroups) => {
              bulkActionWrapper(addEnrolmentsToUserGroups, { userGroups }, 100);
            }}
            errorAddEnrolmentToGroups={bulkActionError}
            onHide={hideModal}
            institutionPath={currentInstitutionPath}
          />
        );
      case ShowedModalType.REMOVE_FROM_INSTITUTION:
        return (
          <RemoveFromInstitutionModal
            error={bulkActionError}
            isLoading={isBulkActionRunning}
            onActionCallback={() => bulkActionWrapper(removeEnrolments, {}, 10)}
            onHide={hideModal}
            totalMembersRemove={totalSelectedMembers}
          />
        );
      case ShowedModalType.CHANGE_INSTITUTION_ROLE:
        return (
          <ChangeInstitutionRoleModal
            error={bulkActionError}
            isLoading={isBulkActionRunning}
            onActionCallback={(role) =>
              bulkActionWrapper(updateRoleForUsers, { role }, 10)
            }
            onHide={hideModal}
            totalMembersAssigned={totalSelectedMembers}
          />
        );
      case ShowedModalType.REMOVE_FROM_COURSES:
        return (
          <RemoveFromCoursesModal
            type="member"
            errorMessage={bulkActionError}
            isRemovingFromCoursesLoading={isBulkActionRunning}
            onHide={hideModal}
            onRemoveFromCourses={(coursePaths) =>
              bulkActionWrapper(removeFromCourse, { coursePaths }, 10, 'userId')
            }
            totalSelectedMembers={totalSelectedMembers}
            courses={allCourses}
          />
        );
      case ShowedModalType.ASSIGN_TO_COURSES:
        return (
          <AssignToCoursesModal
            errorMessage={bulkActionError}
            isAssigningFromCoursesLoading={isBulkActionRunning}
            institutionPath={currentInstitutionPath}
            onAssignToCourses={handleScheduleAssignToCourse}
            onHide={hideModal}
            totalSelectedMembers={totalSelectedMembers}
            courses={allCourses}
          />
        );

      default:
        // Reset error message every modal type change
        setBulkActionError('');
        return <></>;
    }
  }, [
    showedModalType,
    currentInstitutionPath,
    hideModal,
    addEnrolmentsToUserGroups,
    isBulkActionRunning,
    bulkActionWrapper,
    bulkActionError,
    selectAll,
    selectedRows,
    total,
    removeEnrolments,
    updateRoleForUsers,
  ]);

  return (
    <>
      {ShowedModal}
      <FilterWrapper>
        <FilterByUserSelect
          hideToggleButton
          label="Search by name, email or ID"
          placeholder="Start typing..."
          filterOptions={(items) => items}
          options={
            suggestUserQueryResult
              ? suggestUserQueryResult.map((user) => ({
                  // there's a case where user data doesn't have fullName, so it sets the default to empty string
                  label: user.fullName || '',
                  subText: user.profileName || '',
                  value: user.userId,
                }))
              : []
          }
          onSearchChange={(key) => {
            setSearchUserQuery(key);
          }}
          onValueChange={(valuePair) => {
            if (valuePair) {
              setEnrolmentsQueryParams((prev) => ({
                ...prev,
                filterByUserId: valuePair.value,
              }));
            } else {
              setEnrolmentsQueryParams((prev) => ({
                ...prev,
                filterByUserId: undefined,
              }));
            }
          }}
        />
        <InstitutionRoleSelect
          label="Filter by role"
          value={selectedRole}
          options={INSTITUTION_SELECT_ROLE_OPTIONS}
          onValueChange={(role) => {
            if (role) setSelectedRole(role);
          }}
        />
        <CourseAssignedSelect
          label="Filter by courses"
          value={coursesAssignedSelectedValue}
          options={[
            {
              value: 'all',
              label: 'All',
            },
            ...allCourses.map((course) => ({
              label: course.name,
              value: course.path,
            })),
          ]}
          onValueChange={(valuePair) => {
            if (valuePair) {
              setCoursesAssignedSelectedValue(valuePair);
              setEnrolmentsQueryParams((prev) => ({
                ...prev,
                filterByCoursePaths:
                  valuePair.value !== 'all' ? [valuePair.value] : undefined,
              }));
            }
          }}
        />
      </FilterWrapper>
      <ActionTableWrapper>
        {isLoadingData && (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        )}
        <ActionTable
          fixTopHeaders
          fixLeftHeaders
          itemName="member"
          headers={[
            {
              key: 'name_email',
              cell: <TableHeading minWidth={200}>Name</TableHeading>,
            },
            {
              key: 'institution_role',
              cell: <TableHeading>Role</TableHeading>,
            },
            {
              key: 'institutionMembershipId',
              cell: <TableHeading>Team member ID</TableHeading>,
            },
            {
              key: 'courses_assigned',
              cell: <TableHeading>Courses</TableHeading>,
              sortKey: 'courseEnrolmentCount',
            },
            { key: 'groups', cell: 'Groups', sortKey: 'userGroupCount' },
            {
              key: 'last_active',
              cell: <TableHeading>Last active</TableHeading>,
              sortKey: 'lastActive',
            },
          ]}
          rows={activeMembersrows}
          toolbarActions={
            <Popover
              placement="bottom"
              toggle={
                <BulkActionButton>
                  Bulk actions <BulkActionSortDownIcon icon={faSortDown} />
                </BulkActionButton>
              }
              popOverInitialState={{ gutter: 0 }}
              content={
                <BulkActionContentWrapper>
                  <BulkActionContentButton
                    type="button"
                    onClick={() =>
                      setShowedModalType(ShowedModalType.ASSIGN_TO_COURSES)
                    }
                  >
                    <BulkActionIcon fixedWidth icon={faPlus} />
                    Assign to courses
                  </BulkActionContentButton>
                  <BulkActionContentButton
                    type="button"
                    onClick={() =>
                      setShowedModalType(ShowedModalType.REMOVE_FROM_COURSES)
                    }
                  >
                    <BulkActionIcon fixedWidth icon={faMinus} />
                    Remove from courses
                  </BulkActionContentButton>
                  <BulkActionContentButton
                    type="button"
                    onClick={() =>
                      setShowedModalType(
                        ShowedModalType.CHANGE_INSTITUTION_ROLE
                      )
                    }
                  >
                    <BulkActionIcon fixedWidth icon={faCog} />
                    Change institution role
                  </BulkActionContentButton>
                  <BulkActionContentButton
                    type="button"
                    onClick={() =>
                      setShowedModalType(ShowedModalType.ADD_TO_GROUPS)
                    }
                  >
                    <BulkActionIcon fixedWidth icon={faUsers} />
                    Add to groups
                  </BulkActionContentButton>
                  <hr />
                  <BulkActionContentButton
                    type="button"
                    onClick={() =>
                      setShowedModalType(
                        ShowedModalType.REMOVE_FROM_INSTITUTION
                      )
                    }
                  >
                    <BulkActionIcon fixedWidth icon={faTrash} />
                    Remove from institution
                  </BulkActionContentButton>
                </BulkActionContentWrapper>
              }
              withArrow={false}
            />
          }
          selectedRows={selectedRows}
          selectAll={selectAll}
          totalItems={total}
          sortBy={sortBy}
          onSelectedRowsChanged={setSelectedRows}
          onSelectAllChanged={setSelectAll}
          onSortChanged={setSortBy}
        />
      </ActionTableWrapper>
      <PaginationContainer>
        <Paginate
          pageSizeOptions={[10, 25, 50, 100]}
          page={pageIndex}
          totalPages={totalPages}
          pageSize={pageSize}
          totalResults={total}
          handlePageChange={setPageIndex}
          showPageSizeSelector
          handlePageSizeChange={setPageSize}
        />
      </PaginationContainer>
    </>
  );
};
